import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";

import {
    LanguageGlobeIcon,
    languages
} from "@/components/global-footer/language-selector/src/common";
import { wacToken } from "@/libraries/design/foundations";
import { Cross } from "@/libraries/wpl/foundations/src";
import { experiments, useExperiment } from "@/utils/experiment";
import { delocalizePathname, isValidLocale } from "@/utils/localization";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

import LanguageSelector from "./language-selector";

const containerStyles = css({
    "backgroundColor": "rgb(244, 245, 247)",
    "position": "fixed",
    "insetInlineStart": 0,
    "insetInlineEnd": 0,
    "insetBlockEnd": 0,
    "zIndex": 999999,
    "boxShadow": "0 5px 10px",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "textAlign": "center",
    "verticalAlign": "middle",
    '&[data-hidden="true"]': {
        display: "none"
    },
    [media.below.sm]: {
        display: "block",
        textAlign: "left",
        padding: "0"
    }
});

const innerContainerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "90%",
    padding: "5px 36px",
    verticalAlign: "middle",
    [media.below.sm]: {
        display: "block",
        paddingInline: "15px 0px",
        paddingBlock: "0",

        // nested selector needed to apply this style to the globe icon component , which does not accept style/css
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        svg: {
            display: "none"
        }
    }
});

const languageSuggestionStyles = css({
    marginInline: "5px",
    fontSize: "16px",
    display: "inline-block",
    verticalAlign: "middle",
    [media.below.sm]: {
        fontSize: "14px",
        display: "block",
        marginInline: "0px 15px",
        borderBlockEnd: `1px solid ${wacToken("color.neutral.dark900")}`,
        paddingInlineEnd: "15px",
        paddingBlock: "10px"
    }
});

const languageSuggestionLinkStyles = css({
    "borderInlineEnd": "1px solid rgb(193, 199, 208)",
    "paddingInlineEnd": "15px",
    "color": "#0052CC",
    "textDecoration": "none",
    "&:hover": {
        textDecoration: "underline"
    },
    [media.below.sm]: {
        borderInlineEnd: "none"
    }
});

const closeIconStyles = css({
    appearance: "none",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    position: "absolute",
    insetInlineEnd: "16px",
    verticalAlign: "middle",
    display: "inline-block",
    height: "28px",
    [media.below.sm]: {
        padding: "0px",
        insetInlineEnd: "4px",
        insetBlockStart: "4px",
        background: "center center no-repeat"
    }
});

const crossSvgStyles = css({
    // disable eslint because we want to match what's on Magnolia
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#505f79"
});

export const getLocalizedPath = (targetLocale: string): string => {
    const path = window.location.pathname;
    const delocalizedPath = delocalizePathname(path);
    if (delocalizedPath === "") {
        // check homepage
        if (targetLocale === "en") {
            return "/";
        }
        return `/${targetLocale}`;
    }
    if (targetLocale === "en") {
        return delocalizedPath;
    }
    return `/${targetLocale}${delocalizedPath}`;
};

export const LanguageSelectorBanner = () => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [isBannerVisible, setBannerVisible] = useState<boolean>(false);
    const [preferredLocale, setPreferredLocale] = useState<string>("");

    const currentLocale = intl.locale;
    const getPreferredLanguage = (preferredLocale: string) => {
        const preferredLanguage = languages.find(
            (language) => language.value === preferredLocale
        );
        return preferredLanguage ? preferredLanguage.label : "English";
    };

    const gwp5091LanguageSelectorBanner = useExperiment(
        experiments.gwp5091LanguageSelectorBanner
    );

    const getBrowserLocale = (isoCode: string) => {
        const locale = isoCode.substring(0, 2);
        if (locale === "pt") {
            // explicitly map portuguese to brazilian portuguese
            return "br";
        }
        return locale;
    };
    useEffect(() => {
        const checkLocale = () => {
            const storedDismiss = getLocalStorage(
                "language-selector-dismissed"
            );
            if (!storedDismiss) {
                try {
                    const browserLocale = getBrowserLocale(
                        window.navigator.language
                    );
                    if (browserLocale && isValidLocale(browserLocale)) {
                        setPreferredLocale(browserLocale);
                    }
                } catch (error) {
                    console.error("Error getting preferred locale:", error);
                }
            }
        };
        checkLocale();
    }, []);

    useEffect(() => {
        if (preferredLocale === "") {
            return;
        }

        const storedDismiss = getLocalStorage("language-selector-dismissed");
        if (!storedDismiss && currentLocale !== preferredLocale) {
            const languageSelectorBannerEnrolled =
                gwp5091LanguageSelectorBanner.type === "enrolled";
            if (languageSelectorBannerEnrolled) {
                gwp5091LanguageSelectorBanner.fireExposure();
                if (gwp5091LanguageSelectorBanner.value) {
                    setBannerVisible(true);
                }
            }
        }
    }, [currentLocale, gwp5091LanguageSelectorBanner, preferredLocale]);

    const handleDismiss = () => {
        setLocalStorage("language-selector-dismissed", "1");
        setBannerVisible(false);
        // fire analytics event
        createAnalyticsEvent({
            action: "dismissed",
            actionSubject: "languageSelectorBanner",
            eventName: "language-selector-banner-dismissed",
            eventType: "ui",
            attributes: {
                page_locale: currentLocale,
                preferred_locale: preferredLocale,
                trackEventName: "wac.language-selector-banner.dismissed",
                href: window.location.href,
                page_path: window.location.pathname
            }
        }).fire();
    };

    useEffect(() => {
        if (isBannerVisible) {
            document.body.style.paddingBottom = "60px";
        } else {
            document.body.style.paddingBottom = "0";
        }
        return () => {
            document.body.style.paddingBottom = "0";
        };
    }, [isBannerVisible]);

    return (
        <div
            css={containerStyles}
            data-hidden={!isBannerVisible}
            data-testid="language-selector-banner"
        >
            <div css={innerContainerStyles}>
                <LanguageGlobeIcon color={"#42526e"} />
                <div css={languageSuggestionStyles}>
                    <a
                        href={getLocalizedPath(preferredLocale)}
                        css={languageSuggestionLinkStyles}
                        data-testid="language-suggestion-link"
                        onClick={() => {
                            // Fire analytics event when user clicks to change language
                            createAnalyticsEvent({
                                action: "suggestionClicked",
                                actionSubject: "languageSelectorBanner",
                                eventType: "ui",
                                attributes: {
                                    page_locale: currentLocale,
                                    preferred_locale: preferredLocale,
                                    trackEventName:
                                        "wac.language-selector-banner.clicked",
                                    href: window.location.href,
                                    page_path: window.location.pathname
                                }
                            }).fire();
                        }}
                    >
                        <FormattedMessage
                            id="language-selector-banner.view-page"
                            defaultMessage="View this page in {language}?"
                            description={
                                "Text asking if a user wants to view the page in their preferred language."
                            }
                            values={{
                                language: (
                                    <span
                                        css={css({
                                            textDecoration: "underline"
                                        })}
                                    >
                                        {getPreferredLanguage(preferredLocale)}
                                    </span>
                                )
                            }}
                        />
                    </a>
                </div>
                <LanguageSelector
                    currentLocale={currentLocale}
                    preferredLocale={preferredLocale}
                    languages={languages.map((lang) => ({
                        locale: lang.value,
                        localeLabel: lang.label,
                        localeUrl: getLocalizedPath(lang.value)
                    }))}
                />
            </div>
            <button
                data-testid="close-button"
                aria-label={intl.formatMessage({
                    id: "language-selector-banner.close.aria-label",
                    defaultMessage: "Close"
                })}
                css={closeIconStyles}
                onClick={handleDismiss}
            >
                <Cross size="28px" css={crossSvgStyles} />
            </button>
        </div>
    );
};
